(() => {
  if (customElements.get('yahav-maker-feedback')) return;

  const widgetSelector = '.jdgm-review-widget, #judgeme_product_reviews';
  const starColor = '#fbaa1b';

  class MakerFeedback extends HTMLElement {
    connectedCallback() {
      this.stopWatching();
      this.productId = this.dataset.productId?.trim();
      this.productHandle = this.reviewProductHandle(this.dataset.productUrl);
      this.fields = {
        title: this.querySelector('[data-maker-review-title]'),
        body: this.querySelector('[data-maker-review-body]'),
        name: this.querySelector('[data-maker-review-name]'),
        verification: this.querySelector('[data-maker-review-verification]'),
        rating: this.querySelector('[data-maker-review-stars]'),
        stars: [...this.querySelectorAll('[data-maker-review-star] path')]
      };
      if (!this.productId || !this.fields.title || !this.fields.body || !this.fields.name ||
          !this.fields.verification || !this.fields.rating || this.fields.stars.length !== 5) return;
      // A review from the product's metafields is rendered by Liquid and always kept.
      if (this.hasAttribute('data-manual-review')) return;

      if (this.selectedReview && this.selectedProductId === this.productId) {
        this.showReview(this.selectedReview);
        return;
      }

      const embedded = this.querySelector('template[data-maker-review-html]')?.content;
      const review = embedded && this.bestReview([embedded]);
      if (review) {
        this.showReview(review);
        return;
      }

      // Products without their own written review borrow a store-wide one. The product section
      // includes those reviews only in that case, so their presence also means no live wait is needed.
      const storeReview = this.storeReview();
      if (storeReview) {
        this.showReview(storeReview);
        return;
      }

      // Judge.me can hydrate its product widget after the section is rendered.
      // Watch only this product's widget once it exists, and stop after one choice.
      this.liveRoots = new Set();
      this.widgetObserver = new MutationObserver(() => this.scheduleReview());
      this.watchTimeout = window.setTimeout(() => {
        this.tryLiveReview();
        this.stopWatching();
      }, 20000);

      if (this.observeWidgets(document)) {
        this.tryLiveReview();
        return;
      }

      // The app may insert the widget itself later. Discovery is bounded by the
      // same timeout and immediately narrows to a matching product widget.
      this.discoveryObserver = new MutationObserver(records => {
        let found = false;
        for (const record of records) {
          if (record.type === 'attributes') {
            found = this.observeWidgets(record.target) || found;
          } else {
            for (const node of record.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) found = this.observeWidgets(node) || found;
            }
          }
        }
        if (found) {
          this.discoveryObserver?.disconnect();
          this.discoveryObserver = null;
          this.scheduleReview();
        }
      });
      this.discoveryObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-id', 'data-product-id']
      });
    }

    disconnectedCallback() {
      this.stopWatching();
    }

    widgetProductId(widget) {
      return (widget.dataset.productId || widget.dataset.id || '').trim();
    }

    reviewProductHandle(url) {
      if (!url) return '';
      try {
        const pathname = new URL(url, 'https://reviews.invalid').pathname;
        const match = pathname.match(/\/products\/([^/]+)\/?$/);
        return match ? decodeURIComponent(match[1]) : '';
      } catch (_) {
        return '';
      }
    }

    reviewText(element) {
      if (!element) return '';
      const copy = element.cloneNode(true);
      copy.querySelectorAll('script, style, button, [role="button"], .jdgm-rev__body-read-more, .jdgm-rev__body-read-less')
        .forEach(control => control.remove());
      return copy.textContent.trim();
    }

    // Mostly-capitals text reads as shouting in a showcase quote.
    isShouting(text) {
      const letters = text.replace(/[^A-Za-z]/g, '');
      return letters.length > 0 && letters.replace(/[^A-Z]/g, '').length / letters.length > 0.6;
    }

    isPlaceholder(text) {
      return /^this review has no content\.?$/i.test(text);
    }

    observeWidgets(root) {
      const widgets = [...root.querySelectorAll(widgetSelector)];
      if (root.matches?.(widgetSelector)) widgets.unshift(root);
      let found = false;
      for (const widget of widgets) {
        if (this.widgetProductId(widget) !== this.productId) continue;
        found = true;
        if (this.liveRoots.has(widget)) continue;
        this.liveRoots.add(widget);
        this.widgetObserver.observe(widget, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-review-id', 'data-score', 'data-verified-buyer', 'data-product-id']
        });
      }
      return found;
    }

    bestReview(roots) {
      const reviews = [];
      const seen = new Set();
      for (const root of roots) {
        for (const element of root.querySelectorAll('.jdgm-rev[data-review-id]')) {
          const id = element.dataset.reviewId?.trim();
          const explicitProduct = element.dataset.productId?.trim();
          const productUrl = element.dataset.productUrl?.trim();
          const productHandle = this.reviewProductHandle(productUrl);
          const widget = element.closest(widgetSelector);
          const widgetProduct = widget && this.widgetProductId(widget);
          if (!id || seen.has(id) || (explicitProduct && explicitProduct !== this.productId) ||
              (widgetProduct && widgetProduct !== this.productId) ||
              (productUrl && (!productHandle || productHandle !== this.productHandle)) ||
              (widget?.dataset.shopReviews === 'true' && !explicitProduct && !productUrl)) continue;

          const name = element.querySelector('.jdgm-rev__author')?.textContent.trim();
          const bodyElement = element.querySelector('.jdgm-rev__body');
          // The live app can replace the original body with an excerpt. Without
          // a complete source, leave the existing quote rather than copy an excerpt.
          if (bodyElement?.classList.contains('is-truncated')) continue;
          const body = this.reviewText(bodyElement);
          const rating = Number(element.querySelector('.jdgm-rev__rating')?.dataset.score);
          if (!name || !body || this.isPlaceholder(body) || !Number.isInteger(rating) || rating < 1 || rating > 5) continue;
          const title = this.reviewText(element.querySelector('.jdgm-rev__title'));

          seen.add(id);
          reviews.push({
            id,
            name,
            body,
            rating,
            title: this.isPlaceholder(title) ? '' : title,
            verified: element.dataset.verifiedBuyer === 'true'
          });
        }
      }

      // A complete quote comes first, then rating, verified purchase and useful
      // detail. Equal scores preserve Judge.me's existing order.
      reviews.sort((a, b) => Number(Boolean(b.title)) - Number(Boolean(a.title)) ||
        b.rating - a.rating || Number(b.verified) - Number(a.verified) ||
        Math.min(b.body.length, 1000) - Math.min(a.body.length, 1000));
      return reviews[0];
    }

    // Same filter as the gallery review card; offset by one so the two quotes on a page differ.
    storeReview() {
      const template = document.querySelector('template[data-store-reviews]');
      const reviews = [...(template?.content.querySelectorAll('.jdgm-rev[data-review-id]') || [])].map(element => ({
        id: element.dataset.reviewId.trim(),
        name: element.querySelector('.jdgm-rev__author')?.textContent.trim(),
        body: this.reviewText(element.querySelector('.jdgm-rev__body')),
        title: this.reviewText(element.querySelector('.jdgm-rev__title')),
        rating: Number(element.querySelector('.jdgm-rev__rating')?.dataset.score),
        product: element.querySelector('.jdgm-rev__prod-link')?.textContent.trim() || '',
        verified: element.dataset.verifiedBuyer === 'true'
      })).filter(review => review.id && review.name && review.rating === 5 && review.body.length >= 30 &&
        !this.isPlaceholder(review.body) && !/shipping/i.test(review.product) && !this.isShouting(review.body))
        // One review per reviewer, matching the gallery card's list.
        .filter((review, index, list) => list.findIndex(other => other.name.toLowerCase() === review.name.toLowerCase()) === index);
      if (!reviews.length) return null;
      const review = reviews[((Number(this.productId) || 0) + 1) % reviews.length];
      return { ...review, title: this.isPlaceholder(review.title) ? '' : review.title };
    }

    scheduleReview() {
      window.clearTimeout(this.settleTimeout);
      this.settleTimeout = window.setTimeout(() => this.tryLiveReview(), 150);
    }

    tryLiveReview() {
      if (!this.isConnected || !this.liveRoots) return;
      const roots = [...this.liveRoots].filter(root => root.isConnected &&
        this.widgetProductId(root) === this.productId);
      const review = this.bestReview(roots);
      if (review) this.showReview(review);
    }

    showReview(review) {
      this.fields.title.textContent = review.title;
      this.fields.title.hidden = !review.title;
      this.fields.body.textContent = review.body;
      this.fields.name.textContent = review.name;
      this.fields.rating.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      this.fields.stars.forEach((star, index) => {
        const filled = index < review.rating;
        star.setAttribute('fill', filled ? starColor : 'none');
        if (filled) {
          star.removeAttribute('stroke');
          star.removeAttribute('stroke-width');
        } else {
          star.setAttribute('stroke', starColor);
          star.setAttribute('stroke-width', '1.5');
        }
      });
      this.selectedReview = review;
      this.selectedProductId = this.productId;
      this.dataset.makerReviewId = review.id;
      this.stopWatching();
    }

    stopWatching() {
      this.discoveryObserver?.disconnect();
      this.widgetObserver?.disconnect();
      window.clearTimeout(this.watchTimeout);
      window.clearTimeout(this.settleTimeout);
      this.discoveryObserver = null;
      this.widgetObserver = null;
      this.liveRoots = null;
    }
  }

  customElements.define('yahav-maker-feedback', MakerFeedback);
})();
